import { t } from '../../i18n';
import type { ToolCallState } from '../../types';

/** Internal plumbing tool calls the end user never needs to see happen - system tools with no
 *  user-relevant side effect (data-lake queries, history-retrieval lookups). */
export const HIDDEN_TOOL_CALL_NAMES = new Set([
  'request_tool_access', // legacy: no longer sent, but still present in older stored conversations
  'Analyze_Data',
  'Data_Analyst', // the analysis sub-agent is server-side plumbing - only SuperAdmins see it, in the usage view
  'get_full_tool_result',
  'get_full_previous_message',
]);

/** Collapse repeated calls to the same tool (e.g. multi-round agent turns, or several
 *  parallel calls in one turn) into a single pill per tool name: a bare "calling"
 *  placeholder while any instance is still running, or just the last call's own
 *  state once every instance has settled. */
export function selectVisibleToolCalls(toolCalls: ToolCallState[]): ToolCallState[] {
  const order: string[] = [];
  const byName = new Map<string, ToolCallState[]>();
  for (const tc of toolCalls) {
    if (!byName.has(tc.name)) {
      byName.set(tc.name, []);
      order.push(tc.name);
    }
    byName.get(tc.name)!.push(tc);
  }
  return order.map((name) => {
    const calls = byName.get(name)!;
    const pending = calls.find((c) => c.status === 'calling');
    if (pending) return { id: pending.id, name, status: 'calling' };
    return calls[calls.length - 1];
  });
}

export function formatTime(timestamp: number): string {
  const date = new Date(timestamp);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = date.toDateString() === yesterday.toDateString();
  const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (isToday) return `${t('today')} ${time}`;
  if (isYesterday) return `${t('yesterday')} ${time}`;
  return `${date.toLocaleDateString()} ${time}`;
}

export function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
